<script>
    import { selectedItem } from '../../store/store.js';
    import { displayInt, tooltip, safeDivide } from '../../utils/utils.js';
    import Gauge from '../common/Gauge.svelte';
    import './shared.css';

    function displayOptionalInt(value) {
        return value != null ? displayInt(value) : '-';
    }

    function displayTypkod(typkod) {
        if (!typkod) return '-';
        const typkodMap = {
            '320': 'Hyreshusenhet, huvudsakligen bostäder',
            '321': 'Hyreshusenhet, bostäder och lokaler',
            '325': 'Hyreshusenhet, specialbyggnad',
        };
        return typkodMap[typkod] || 'Okänd typkod';
    }

    const kravNybyggnad = 90;

    function getEnergyLevel(ep) {
        if (ep === null || ep === undefined) return 4; // Default level
        const percentage = (ep / kravNybyggnad) * 100;

        if (percentage <= 50) return 1; // A
        if (percentage <= 75) return 2; // B
        if (percentage <= 100) return 3; // C
        if (percentage <= 135) return 4; // D
        if (percentage <= 180) return 5; // E
        if (percentage <= 235) return 6; // F
        return 7; // G
    }

    function getTotalEnergyLevel(value) {
        if (value === null || value === undefined) return 4; // Default level

        if (value < 70) return 1; // Mycket Bra
        if (value <= 89) return 2; // Bra
        if (value <= 109) return 3; // Godkänd
        if (value <= 140) return 4; // Normal / Genomsnitt
        if (value <= 175) return 5; // Under Medel
        if (value <= 225) return 6; // Dålig
        return 7; // Mycket Dålig
    }

    function getTotalElectricityLevel(value) {
        if (value === null || value === undefined) return 4; // Default level

        if (value < 8) return 1; // Mycket Bra
        if (value <= 11) return 2; // Bra
        if (value <= 14) return 3; // Godkänd
        if (value <= 20) return 4; // Normal
        if (value <= 30) return 5; // Under Medel
        if (value <= 50) return 6; // Dålig
        return 7; // Mycket Dålig
    }

    function getDistrictHeatingLevel(value) {
        if (value === null || value === undefined) return 4; // Default level

        if (value < 60) return 1; // Mycket Bra
        if (value <= 74) return 2; // Bra
        if (value <= 89) return 3; // Godkänd
        if (value <= 115) return 4; // Normal / Genomsnitt
        if (value <= 140) return 5; // Under Medel
        if (value <= 170) return 6; // Dålig
        return 7; // Mycket Dålig
    }

    function getHotWaterLevel(value) {
        if (value === null || value === undefined) return 4; // Default level

        if (value < 12) return 1; // Mycket Bra
        if (value <= 15) return 2; // Bra
        if (value <= 19) return 3; // Godkänd
        if (value <= 24) return 4; // Normal / Genomsnitt
        if (value <= 29) return 5; // Under Medel
        if (value <= 35) return 6; // Dålig
        return 7; // Mycket Dålig
    }

    function getRadonLevel(value) {
        if (value === null || value === undefined) return 4; // Default level

        if (value < 21) return 1; // Mycket Bra
        if (value <= 50) return 2; // Bra
        if (value <= 80) return 3; // Godkänd
        if (value <= 150) return 4; // Normal / Genomsnitt
        if (value <= 200) return 5; // Förhöjd Risk / Bör Utredas
        if (value <= 400) return 6; // Dålig / Åtgärd Krävs
        return 7; // Mycket Dålig / Akut Åtgärd
    }

    $: totalEnergyPerSqm = $selectedItem?.total_energy_consumption && $selectedItem?.atemp 
        ? $selectedItem.total_energy_consumption / $selectedItem.atemp 
        : null;

    $: totalElectricityPerSqm = $selectedItem?.total_electricity_consumption && $selectedItem?.atemp 
        ? $selectedItem.total_electricity_consumption / $selectedItem.atemp 
        : null;

    $: districtHeatingPerSqm = $selectedItem?.district_heating_consumption && $selectedItem?.atemp 
        ? $selectedItem.district_heating_consumption / $selectedItem.atemp 
        : null;

    $: hotWaterPerSqm = $selectedItem?.energy_consumption_for_hot_water_preparation && $selectedItem?.atemp 
        ? $selectedItem.energy_consumption_for_hot_water_preparation / $selectedItem.atemp 
        : null;
</script>

{#if $selectedItem?.energy_declaration_id}
<div class="energy-wrapper">
    <div class="energy-box">
        <div class="header-card">
            <h5><i class="fa-solid fa-bolt fa-sm icon-detail"></i> Energi & Förbrukning</h5>
        </div>
        <div class="energy-content-grid">
            {#if $selectedItem.energiprestanda}
            <div class="metric-card energiprestanda-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-gauge-high fa-sm icon-detail"></i> Energiprestanda</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Vi har tagit fram en egen skala för att enkelt kunna jämföra och klassificera energiprestandan specifikt för bostadsrättsföreningar i Sverige. Skalan är tänkt att vara mer lättolkad än den officiella och är direkt baserad på verkliga värden från svenska BRF:er, där ett genomsnitt ligger runt 119 kWh/m² per år.</p>
                            <p>Ett lägre värde innebär en mer energieffektiv byggnad med lägre driftkostnader.</p>
                            <br>
                            <p><strong>Nivåer:</strong></p>
                            <ul>
                                <li><strong>Mycket Bra (&lt; 60):</strong> Toppklass. Exceptionellt låg energianvändning. Ofta moderna fastigheter med den senaste tekniken, t.ex. solceller eller bergvärme.</li>
                                <li><strong>Bra (60 – 80):</strong> Mycket energieffektiv. Tydligt bättre än en nybyggd fastighet. Ofta resultatet av omfattande energirenoveringar.</li>
                                <li><strong>Godkänd (81 – 100):</strong> Bra prestanda. En solid och energieffektiv fastighet, ofta i nivå med moderna byggkrav. Låga driftkostnader.</li>
                                <li><strong>Normal / Genomsnitt (101 – 135):</strong> Här ligger genomsnittet. Detta är en normal och acceptabel nivå för många svenska BRF:er, men det finns ofta god potential för förbättring.</li>
                                <li><strong>Under Medel (136 – 165):</strong> Något högre förbrukning. Typiskt för en äldre, orenoverad fastighet. Energieffektiviserande åtgärder skulle ge märkbar effekt.</li>
                                <li><strong>Dålig (166 – 200):</strong> Hög förbrukning. Indikerar att fastigheten har betydande värmeförluster, vilket leder till höga energikostnader. Åtgärder bör övervägas.</li>
                                <li><strong>Mycket Dålig (&gt; 200):</strong> Mycket hög förbrukning. Fastigheten är inte energieffektiv. Här finns stor besparingspotential men sannolikt ett stort renoveringsbehov.</li>
                            </ul>
                            <p><strong>Vad man vill se:</strong></p>
                            <p>Målet för de flesta föreningar är att röra sig nedåt i tabellen, mot en lägre siffra. För en styrelse är denna skala ett utmärkt verktyg för att identifiera vilka fastigheter som har störst besparingspotential och var insatser gör mest nytta för både ekonomin och miljön.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    <Gauge 
                        value={$selectedItem.energiprestanda} 
                        level={getEnergyLevel($selectedItem.energiprestanda)}
                        unit="kWh/m²" 
                    />
                </div>
            </div>
            {/if}

            {#if totalEnergyPerSqm && $selectedItem.total_energy_consumption}
            <div class="metric-card energiprestanda-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-fire fa-sm icon-detail"></i> Total energianvändning</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Till skillnad från energiprestanda, som endast mäter energin för uppvärmning, varmvatten och fastighetsel, inkluderar total energikonsumtion även de boendes individuella hushållsel.</p>
                            <p>Detta mått ger den fullständiga bilden av all energi som förbrukas inom fastighetens väggar per kvadratmeter och år. Det speglar både byggnadens effektivitet och de boendes vanor.</p>
                            <p>Vi har skapat en egen skala för att enkelt klassificera denna totala förbrukning, baserad på verkliga värden från bostadsrättsföreningar i Sverige, där genomsnittet ligger på 125 kWh/m² och år.</p>
                            <br>
                            <p><strong>Nivåer:</strong></p>
                            <ul>
                                <li><strong>Mycket Bra (&lt; 70):</strong> Elitnivå. Exceptionellt låg totalförbrukning. Kräver en extremt energieffektiv fastighet (t.ex. passivhus) i kombination med mycket energimedvetna boende.</li>
                                <li><strong>Bra (70 – 89):</strong> Mycket effektiv. En kombination av en välbyggd fastighet och boende med låg elförbrukning. Tydligt bättre än genomsnittet.</li>
                                <li><strong>Godkänd (90 – 109):</strong> Bra och hållbar nivå. Fastigheten är effektiv och de boendes förbrukning är måttlig. En stark siffra att visa upp.</li>
                                <li><strong>Normal / Genomsnitt (110 – 140):</strong> Här ligger genomsnittet (125). Detta är en vanlig och fullt acceptabel nivå för en svensk BRF. Det finns ofta utrymme för förbättring, både i fastigheten och hos de boende.</li>
                                <li><strong>Under Medel (141 – 175):</strong> Högre än genomsnittet. Kan bero på en äldre fastighet, ineffektiva installationer eller att de boende har en hög elförbrukning.</li>
                                <li><strong>Dålig (176 – 225):</strong> Hög totalförbrukning. En tydlig signal om att både fastighetens skal och de boendes vanor bidrar till höga kostnader och stor miljöpåverkan.</li>
                                <li><strong>Mycket Dålig (&gt; 225):</strong> Ohållbart hög förbrukning. En extremt hög siffra som indikerar ett akut behov av åtgärder, både tekniska (renovering) och beteendemässiga (information till boende).</li>
                            </ul>
                            <p><strong>Vad man vill se:</strong></p>
                            <p>Målet är alltid att ha en så låg siffra som möjligt. En siffra i den lägre delen av skalan ("Godkänd" eller bättre) tyder på en välskött förening med låga driftkostnader och en stark miljöprofil. För en styrelse är detta mått perfekt för att se den totala potentialen för besparingar, eftersom det fångar upp allt från värmesystemets effektivitet till de boendes val av vitvaror och belysning.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    <Gauge 
                        value={Math.round(totalEnergyPerSqm)} 
                        level={getTotalEnergyLevel(totalEnergyPerSqm)}
                        unit="kWh/år/kvm" 
                    />
                    <div class="gauge-total-value">
                        {displayOptionalInt($selectedItem.total_energy_consumption)} kWh/år
                    </div>
                </div>
            </div>
            {/if}

            {#if totalElectricityPerSqm && $selectedItem.total_electricity_consumption}
            <div class="metric-card energiprestanda-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-plug fa-sm icon-detail"></i> Total elförbrukning</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Detta mått visar all el som förbrukas i fastigheten per kvadratmeter och år (kWh/m²/år). Det är summan av två delar:</p>
                            <p><strong>Fastighetsel:</strong> El som används i gemensamma utrymmen och för fastighetens drift. Exempel är belysning i trapphus, hissar, ventilationsfläktar och motorvärmare.</p>
                            <p><strong>Hushållsel:</strong> De boendes privata elförbrukning i sina lägenheter för t.ex. vitvaror, belysning, TV och datorer.</p>
                            <p>Denna siffra ger en mycket bra bild av hur el-effektiv fastigheten är och hur energimedvetna de boende är. Vi har skapat en egen skala baserad på verkliga värden från svenska bostadsrättsföreningar, där genomsnittet ligger på 13,8 kWh/m² och år.</p>
                            <p>(Observera: Denna siffra är normalt låg eftersom den oftast inte inkluderar el för uppvärmning. Om en fastighet värms med direktverkande el blir värdet betydligt högre).</p>
                            <br>
                            <p><strong>Nivåer:</strong></p>
                            <ul>
                                <li><strong>Mycket Bra (&lt; 8):</strong> Föredömligt. En kombination av extremt energieffektiva installationer (t.ex. LED-belysning med närvarostyrning) och mycket energimedvetna boende.</li>
                                <li><strong>Bra (8 – 11):</strong> Mycket effektivt. Både fastighetens och de boendes elanvändning är låg. En stark siffra som visar på ett aktivt hållbarhetsarbete.</li>
                                <li><strong>Godkänd (12 – 14):</strong> Här ligger genomsnittet (13,8). En fullt normal och bra nivå för en svensk BRF.</li>
                                <li><strong>Normal (15 – 20):</strong> Standardnivå. En vanlig siffra som indikerar att det finns viss potential att spara el, både i gemensamma utrymmen och i lägenheterna.</li>
                                <li><strong>Under Medel (21 – 30):</strong> Något hög förbrukning. Kan bero på äldre belysning, ineffektiv ventilation eller att de boende har en högre elförbrukning än snittet.</li>
                                <li><strong>Dålig (31 – 50):</strong> Hög elförbrukning. En tydlig indikation på att det finns stora besparingar att göra. Gamla fläktar, ineffektiv belysning eller hög individuell förbrukning är troliga orsaker.</li>
                                <li><strong>Mycket Dålig (&gt; 50):</strong> Extremt hög förbrukning. Denna nivå är ohållbar och pekar på ett stort behov av att se över både fastighetens elsystem och att informera de boende.</li>
                            </ul>
                            <p><strong>Vad man vill se:</strong></p>
                            <p>Man vill alltid ha en så låg siffra som möjligt. Detta mått är extra intressant för en styrelse eftersom det visar på besparingspotential inom två områden:</p>
                            <p><strong>Tekniska åtgärder:</strong> Föreningen kan själv påverka fastighetselen genom att byta till LED-belysning, installera närvarostyrning eller byta ut gamla fläktar och pumpar.</p>
                            <p><strong>Information:</strong> Eftersom hushållselen är en stor del av kakan kan föreningen minska den totala förbrukningen genom att informera och engagera de boende i att spara el.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    <Gauge 
                        value={Math.round(totalElectricityPerSqm)} 
                        level={getTotalElectricityLevel(totalElectricityPerSqm)}
                        unit="kWh/år/kvm" 
                    />
                    <div class="gauge-total-value">
                        {displayOptionalInt($selectedItem.total_electricity_consumption)} kWh/år
                    </div>
                </div>
            </div>
            {/if}

            {#if hotWaterPerSqm && $selectedItem.energy_consumption_for_hot_water_preparation}
            <div class="metric-card energiprestanda-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-shower fa-sm icon-detail"></i> Varmvattenförbrukning</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Denna siffra visar hur mycket energi (i kWh/m² och år) som specifikt används för att värma upp tappvarmvatten i fastigheten. Detta är en av de största energikostnaderna efter uppvärmning och påverkas av två huvudsakliga faktorer:</p>
                            <p><strong>De boendes vanor:</strong> Hur ofta och hur länge man duschar, diskar och använder varmvatten.</p>
                            <p><strong>Systemets effektivitet:</strong> Hur välisolerade rören är och hur effektivt varmvattencirkulationssystemet (VVC) är, som ser till att man snabbt får varmvatten i kranen. Ett ineffektivt system leder till stora värmeförluster.</p>
                            <p>Vi har skapat en anpassad skala för att tydliggöra förbrukningen, baserad på verkliga värden från svenska bostadsrättsföreningar där ett genomsnitt ligger på 21,8 kWh/m² och år.</p>
                            <br>
                            <p><strong>Nivåer:</strong></p>
                            <ul>
                                <li><strong>Mycket Bra (&lt; 12):</strong> Föredömligt. Extremt låg förbrukning. Tyder på en kombination av mycket energimedvetna boende och ett toppmodernt, välisolerat VVC-system.</li>
                                <li><strong>Bra (12 – 15):</strong> Mycket effektivt. En förbrukning som ligger tydligt under genomsnittet. Föreningen har sannolikt bra installationer och boende som är medvetna om sin förbrukning.</li>
                                <li><strong>Godkänd (16 – 19):</strong> Effektivt och välskött. En bra nivå som är bättre än genomsnittet. Indikerar ett väl fungerande system utan större värmeförluster.</li>
                                <li><strong>Normal / Genomsnitt (20 – 24):</strong> Här ligger genomsnittet (21,8). Detta är en helt normal och standardmässig nivå för en svensk BRF. Det finns ofta potential för effektivisering av systemet.</li>
                                <li><strong>Under Medel (25 – 29):</strong> Något förhöjd förbrukning. Kan indikera ett äldre eller dåligt injusterat VVC-system som drar onödigt mycket energi, eller en hög förbrukning hos de boende.</li>
                                <li><strong>Dålig (30 – 35):</strong> Hög förbrukning. En tydlig signal på att det finns betydande värmeförluster i varmvattensystemet, vilket leder till onödiga kostnader för föreningen.</li>
                                <li><strong>Mycket Dålig (&gt; 35):</strong> Ohållbart hög förbrukning. Indikerar ett stort slöseri med energi. Här finns ett stort behov av att se över systemet, från rörisolering till cirkulationspumpens inställningar.</li>
                            </ul>
                            <p><strong>Vad man vill se:</strong></p>
                            <p>Man vill se en så låg siffra som möjligt. För en styrelse är detta ett mycket viktigt nyckeltal att hålla koll på. En hög förbrukning kan ofta åtgärdas med relativt enkla medel som att justera VVC-systemet, isolera rör eller installera moderna, snålspolande kranar och duschmunstycken. Eftersom varmvattenförbrukningen också beror på de boendes vanor, kan informationskampanjer vara ett effektivt sätt att sänka kostnaderna för hela föreningen.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    <Gauge 
                        value={Math.round(hotWaterPerSqm)} 
                        level={getHotWaterLevel(hotWaterPerSqm)}
                        unit="kWh/år/kvm" 
                    />
                    <div class="gauge-total-value">
                        {displayOptionalInt($selectedItem.energy_consumption_for_hot_water_preparation)} kWh/år
                    </div>
                </div>
            </div>
            {/if}

            {#if $selectedItem.radon_concentration}
            <div class="metric-card energiprestanda-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-atom fa-sm icon-detail"></i> Radonkoncentration</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Radon är en osynlig och luktfri radioaktiv gas som bildas när grundämnet radium sönderfaller. Den kan komma in i våra bostäder från tre huvudsakliga källor:</p>
                            <ul>
                                <li><strong>Marken:</strong> Den absolut vanligaste källan.</li>
                                <li><strong>Byggmaterial:</strong> Vissa äldre material, framför allt "blåbetong" (blågrå lättbetong), kan avge radon.</li>
                                <li><strong>Vattnet:</strong> Förekommer främst i hushåll med egna borrade brunnar.</li>
                            </ul>
                            <p>Radon är den näst vanligaste orsaken till lungcancer efter rökning. Koncentrationen mäts i Becquerel per kubikmeter luft (Bq/m³).</p>
                            <p>I Sverige finns en nationell referensnivå på 200 Bq/m³ som ett årsmedelvärde. Det innebär att om en mätning visar värden över detta, måste fastighetsägaren vidta åtgärder för att sänka nivån. Vår anpassade skala är byggd kring denna viktiga hälsogräns.</p>
                            <br>
                            <p><strong>Nivåer:</strong></p>
                            <ul>
                                <li><strong>Mycket Bra (&lt; 21):</strong> Utmärkt. Värden på denna nivå anses vara bakgrundsstrålning och utgör ingen anledning till oro.</li>
                                <li><strong>Bra (21 – 50):</strong> Mycket låga nivåer. Tydligt under genomsnittet och väl under alla rekommendationer. En mycket säker inomhusmiljö.</li>
                                <li><strong>Godkänd (51 – 80):</strong> En bra och säker nivå. Fortfarande klart under genomsnittet. Ingen anledning till oro, fastigheten är tät och/eller byggd med radonfria material.</li>
                                <li><strong>Normal / Genomsnitt (81 – 150):</strong> Här ligger genomsnittet (93,8). En acceptabel nivå som är vanlig i många svenska fastigheter. Visar att radon finns, men i koncentrationer som inte anses vara skadliga.</li>
                                <li><strong>Förhöjd Risk / Bör Utredas (151 – 200):</strong> Nära referensnivån. Även om det inte krävs omedelbara åtgärder är detta en varningssignal. Det är klokt att följa upp och planera för eventuella framtida åtgärder.</li>
                                <li><strong>Dålig / Åtgärd Krävs (201 – 400):</strong> Över referensnivån. Detta är en otillfredsställande nivå. Föreningen är skyldig att utreda orsaken och vidta åtgärder för att sänka radonhalten till under 200 Bq/m³.</li>
                                <li><strong>Mycket Dålig / Akut Åtgärd (&gt; 400):</strong> Kraftigt förhöjda värden. Detta utgör en hälsorisk för de boende. Åtgärder bör prioriteras och genomföras skyndsamt.</li>
                            </ul>
                            <p><strong>Vad man vill se:</strong></p>
                            <p>Det absoluta målet är att ha ett årsmedelvärde som ligger under 200 Bq/m³. Ju lägre, desto bättre.</p>
                            <p>För en styrelse är det lagstadgad skyldighet att känna till fastighetens radonvärden och att agera om de är förhöjda. Vanliga och effektiva åtgärder för att sänka radonhalten är:</p>
                            <ul>
                                <li>Förbättra ventilationen (t.ex. installera eller justera mekanisk ventilation).</li>
                                <li>Täta sprickor och otätheter i grunden och källaren.</li>
                                <li>Installera en radonsug eller radonbrunn under fastigheten.</li>
                            </ul>
                            <p>Radon är ett problem som nästan alltid går att åtgärda. Att ha låga radonvärden är en viktig trygghetsfaktor för de boende och en kvalitetsstämpel för föreningen.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    <Gauge 
                        value={Math.round($selectedItem.radon_concentration)} 
                        level={getRadonLevel($selectedItem.radon_concentration)}
                        maxValue={500}
                        unit="Bq/m³" 
                    />
                </div>
            </div>
            {/if}

            {#if districtHeatingPerSqm && $selectedItem.district_heating_consumption}
            <div class="metric-card energiprestanda-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-city fa-sm icon-detail"></i> Fjärrvärmeförbrukning</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Fjärrvärmeförbrukningen är oftast den absolut största enskilda energiposten för en bostadsrättsförening som inte använder el för uppvärmning. Denna siffra (mätt i kWh/m² och år) visar hur mycket energi som går åt för att:</p>
                            <ul>
                                <li>Värma upp fastigheten.</li>
                                <li>Producera varmvatten till de boende.</li>
                            </ul>
                            <p>Ett lågt värde här är det tydligaste kvittot på att fastigheten har ett bra klimatskal (väggar, fönster, tak) och ett effektivt värmesystem.</p>
                            <p>Vi har skapat en egen skala för att enkelt jämföra fjärrvärmeförbrukningen mellan olika bostadsrättsföreningar i Sverige, där genomsnittet ligger på 94,5 kWh/m² och år.</p>
                            <br>
                            <p><strong>Nivåer:</strong></p>
                            <ul>
                                <li><strong>Mycket Bra (&lt; 60):</strong> Toppklass. En extremt välisolerad fastighet, ofta med modern värmeåtervinning (FTX-ventilation). Mycket låga värmekostnader.</li>
                                <li><strong>Bra (60 – 74):</strong> Mycket energieffektiv. Tydligt lägre än snittet. Indikerar ofta en nyare fastighet eller en som genomgått stora renoveringar som fönsterbyte och tilläggsisolering.</li>
                                <li><strong>Godkänd (75 – 89):</strong> Bra och hållbar nivå. En solid fastighet med ett väl fungerande och injusterat värmesystem. Bättre än genomsnittet.</li>
                                <li><strong>Normal / Genomsnitt (90 – 115):</strong> Här ligger genomsnittet (94,5). Detta är en standardnivå för många svenska BRF:er. En acceptabel siffra, men det finns nästan alltid potential för besparingar.</li>
                                <li><strong>Under Medel (116 – 140):</strong> Märkbart högre förbrukning. Kan tyda på äldre fönster, bristfällig isolering eller ett ineffektivt värmesystem som behöver ses över.</li>
                                <li><strong>Dålig (141 – 170):</strong> Hög värmeförbrukning. En tydlig signal om att fastigheten läcker mycket värme, vilket resulterar i onödigt höga kostnader för föreningen.</li>
                                <li><strong>Mycket Dålig (&gt; 170):</strong> Ohållbart hög förbrukning. Indikerar ett stort och sannolikt akut behov av renovering av fastighetens klimatskal och värmesystem för att få ner kostnaderna.</li>
                            </ul>
                            <p><strong>Vad man vill se:</strong></p>
                            <p>Eftersom uppvärmning är den största driftkostnaden för de flesta föreningar, är en låg siffra här direkt kopplad till en stark och stabil ekonomi. För en styrelse är detta nyckeltal det viktigaste att följa över tid. En minskning av fjärrvärmeförbrukningen ger omedelbar och stor effekt på föreningens resultat. För boende och köpare är ett lågt värde en stark indikation på en välskött förening med god kontroll över sina kostnader.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    <Gauge 
                        value={Math.round(districtHeatingPerSqm)} 
                        level={getDistrictHeatingLevel(districtHeatingPerSqm)}
                        unit="kWh/år/kvm" 
                    />
                    <div class="gauge-total-value">
                        {displayOptionalInt($selectedItem.district_heating_consumption)} kWh/år
                    </div>
                </div>
            </div>
            {/if}

            {#if $selectedItem.atemp}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-ruler-combined fa-sm icon-detail"></i> Atemp</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Area i kvadratmeter.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    {displayOptionalInt($selectedItem.atemp)} m²
                </div>
            </div>
            {/if}

            {#if $selectedItem.typkod}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-hashtag fa-sm icon-detail"></i> Typkod</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p><strong>{$selectedItem.typkod}: {displayTypkod($selectedItem.typkod)}</strong></p>
                            <p>En kod från Lantmäteriet som beskriver fastighetens användning. Vanliga koder för flerbostadshus är 320 och 321.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    {$selectedItem.typkod}
                </div>
            </div>
            {/if}

            {#if $selectedItem.heating_system}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-temperature-three-quarters fa-sm icon-detail"></i> Uppvärmningssystem</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p>Huvudsakligt system för uppvärmning av fastigheten. Exempel är fjärrvärme, bergvärme eller gaspanna.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    {$selectedItem.heating_system}
                </div>
            </div>
            {/if}

            {#if $selectedItem.type_of_ventilation_system}
            <div class="metric-card">
                <div class="metric-header">
                    <h6><i class="fa-solid fa-fan fa-sm icon-detail"></i> Typ av ventilation</h6>
                    <div class="tooltip-container" use:tooltip>
                        <span class="info-icon"></span>
                        <div class="tooltip-content">
                            <p><strong>{$selectedItem.type_of_ventilation_system}: {$selectedItem.type_of_ventilation_system}</strong></p>
                            <p>En kod från Lantmäteriet som beskriver fastighetens användning. Vanliga koder för flerbostadshus är 320 och 321.</p>
                        </div>
                    </div>
                </div>
                <div class="metric-value">
                    {$selectedItem.type_of_ventilation_system}
                </div>
            </div>
            {/if}
        </div>
    </div>
</div>
{/if}

<style>
    .energy-wrapper {
        width: 100%;
    }

    .energy-box {
        background: white;
        backdrop-filter: blur(30px) saturate(120%);
        -webkit-backdrop-filter: blur(30px) saturate(120%);
        border-radius: 24px;
        padding: 1.5rem;
        border: none;
        box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.06),
            inset 0 1px 0 rgba(255, 255, 255, 0.3),
            inset 0 -1px 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(255, 255, 255, 0.15);
    }

    .energy-content-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 20px;
        align-items: start;
    }

    @media (max-width: 768px) {
        .energy-content-grid {
            grid-template-columns: 1fr;
        }
    }

    .energiprestanda-card {
        max-width: 100%;
    }


    .metric-card {
        text-align: left;
    }

    .metric-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 12px;
    }

    .metric-header h6 {
        margin: 0;
        font-size: 0.9rem;
        color: #666;
        font-weight: 500;
    }

    .gauge-total-value {
        text-align: center;
        font-size: 1.1rem;
        font-weight: 600;
        color: #333;
        margin-top: 1rem;
    }
</style>

